import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarPlus, FileUp } from 'lucide-react';
import { SLOT_LABEL, STATUS_LABEL } from '@nu/shared';
import { api } from '../../api/client.js';
import { teamColours } from '../matchups/util.js';
import BookingModal from './BookingModal.jsx';
import ScheduleImport from './ScheduleImport.jsx';

// What both Bookings pages share: the booking types, teams and people they
// show, the page heading with New booking, and the booking pop-up.

export function addDays(day, n) {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function mondayOf(day) {
  const d = new Date(`${day}T12:00:00Z`);
  return addDays(day, -((d.getUTCDay() + 6) % 7));
}

// "Kevin C" from "Kevin Caldwell".
export function shortName(name = '') {
  const [first, ...rest] = name.trim().split(/\s+/);
  return rest.length ? `${first} ${rest.at(-1)[0]}` : first;
}

export const LEAVE_LABEL = { all: 'off', am: 'off AM', pm: 'off PM' };

// Calendar chips show only the slot and NU reference; the rest is in the tooltip.
export const SLOT_SHORT = { am: 'AM', pm: 'PM', all_day: 'All' };
export function chipTitle(b) {
  return [
    `${b.reference} · ${SLOT_LABEL[b.slot] || 'No slot'} · ${STATUS_LABEL[b.status]}`,
    b.revisitOf ? `Revisit (defect) of ${b.revisitOf.reference}: half a slot` : null,
    [b.addressLine1, b.town, b.postcode].filter(Boolean).join(', '),
    b.customerName,
  ].filter(Boolean).join('\n');
}

// How full a team's day is against its target, as a colour: red up to a third
// (1-2 of 6), orange up to two thirds (3-4), yellow nearly full (5), green
// full (6 or more). An empty day, or a team with no target, has none.
export function fillLevel(used, target) {
  if (!target || !used) return null;
  const r = used / target;
  if (r >= 1) return 'full';
  if (r > 2 / 3) return 'high';
  if (r > 1 / 3) return 'mid';
  return 'low';
}
export const FILL_LEGEND = [
  { level: 'low', label: '1–2 of 6' },
  { level: 'mid', label: '3–4' },
  { level: 'high', label: '5' },
  { level: 'full', label: 'Full' },
];

// Cancelled and no-access visits don't use up a day's capacity.
export const COUNTS = (b) => b.status !== 'cancelled' && b.status !== 'no_access';
// How much of a day's capacity bookings use: a revisit (a defect) is half a slot.
export const slotsUsed = (list) => list.reduce((n, b) => n + (COUNTS(b) ? (b.revisitOf ? 0.5 : 1) : 0), 0);
export const slotsText = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export function useBookingRefs() {
  const [types, setTypes] = useState([]);
  const [teams, setTeams] = useState([]);
  const [people, setPeople] = useState([]);
  useEffect(() => {
    api('/bookings/types').then((r) => setTypes(r.types), () => {});
    api('/matchups').then((r) => { setTeams(r.teams); setPeople(r.people); }, () => {});
  }, []);
  const colours = useMemo(() => teamColours(teams), [teams]);
  return { types, teams, people, colours };
}

// Days off (from BrightHR) between two dates; [] while loading or offline.
export function useLeave(from, to) {
  const [leave, setLeave] = useState([]);
  useEffect(() => {
    if (!from || !to) return undefined;
    let live = true;
    api(`/leave?from=${from}&to=${to}`).then((r) => { if (live) setLeave(r.leave); }, () => { if (live) setLeave([]); });
    return () => { live = false; };
  }, [from, to]);
  return leave;
}

// Heading, New booking, notice line and the booking pop-up. `reload` runs
// after a save. Returns the page parts and the openers.
export function useBookingEditor({ canEdit, types, teams, reload }) {
  const [open, setOpen] = useState(null); // booking id, or { new: prefill }
  const [notice, setNotice] = useState(null);
  const openBooking = useCallback((id) => setOpen(id), []);
  const openNew = useCallback((prefill = {}) => setOpen({ new: prefill }), []);

  const modal = open && (
    <BookingModal
      id={typeof open === 'object' ? null : open}
      prefill={typeof open === 'object' ? open.new : undefined}
      types={types}
      teams={teams}
      canEdit={canEdit}
      onClose={() => setOpen(null)}
      onSaved={(text, newId) => {
        setNotice({ tone: 'ok', text });
        reload();
        if (newId) setOpen(newId);
      }}
    />
  );
  return { notice, setNotice, openBooking, openNew, modal };
}

// onImported: reload after a Network Plus schedule is imported.
export function BookingsHead({ title, intro, canEdit, onNew, notice, onImported }) {
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(null);
  return (
    <section className="card">
      <div className="page-head">
        <div>
          <h1>{title}</h1>
          <p className="muted">{intro}</p>
        </div>
        {canEdit && (
          <div className="head-actions">
            <button type="button" className="btn with-icon" onClick={() => setImporting(true)}><FileUp size={18} aria-hidden="true" /> Import schedule</button>
            <button type="button" className="btn primary with-icon" onClick={() => onNew()}><CalendarPlus size={18} aria-hidden="true" /> New booking</button>
          </div>
        )}
      </div>
      {notice && <p className={`notice-line notice-${notice.tone}`} role="status">{notice.text}</p>}
      {done && <p className="notice-line notice-ok" role="status">{done}</p>}
      {importing && <ScheduleImport onClose={() => setImporting(false)} onDone={(text) => { setImporting(false); setDone(text); onImported?.(); }} />}
    </section>
  );
}
